/* eslint @typescript-eslint/no-explicit-any: 0 */
import React from 'react';
import { useSelector } from 'react-redux';
import { omit } from 'lodash/fp';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import NewDonationDialog from '@/components/common/contribution/NewContributionDialog';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import BulkEditBoardsDialog from '@/modules/BulkEdit/components/BulkEditBoardsDialog';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import BulkEditLabelsDialog from '@/modules/BulkEdit/components/BulkEditLabelsDialog';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import CreateEditCampaignEventDialog from '@/modules/Campaigns/components/CreateEditCampaignEventDialog';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import CreateEditTicketOptionDialog from '@/modules/Campaigns/components/CreateEditTicketOptionDialog';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import MoveCampaignEventDialog from '@/modules/Campaigns/components/MoveCampaignEventDialog';
import {
  ConstituentExportChoiceDialog,
  ConstituentExportCSVDialog,
  ConstituentExportDestinationDialog,
} from '@/modules/ConstituentExport';
import {
  CreateEditOpportunityDialog,
  LinkToOpportunityDialog,
} from '@/modules/Opportunities';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import CreateCustomFieldDialog from '@/modules/Settings/components/CustomFields/CreateCustomFieldDialog';
import type RootState from '@/types/RootState';
import Dialogs from '../DialogConstants';
import ConfirmationDialog from './ConfirmationDialog/ConfirmationDialog';
import ConstituentLifecyclePositionsDialog from './ConstituentLifecyclePositionsDialog/ConstituentLifecyclePositionsDialog';
import CreateLifecycleBoardDialog from './CreateLifecycleBoardDialog/CreateLifecycleBoardDialog';
import EditLabelDialog from './EditLabelDialog/EditLabelDialog';
import ExportMessageDialog from './ExportMessageDialog/ExportMessageDialog';
import InformationalDialog from './InformationalDialog/InformationalDialog';
import LinkToDonationsDialog from './LinkToDonationsDialog';
import LinkToPledgeDialog from './LinkToPledgeDialog';
import PledgeIntegrationDialog from './PledgeIntegrationDialog';

type LoggedInDialogConfig = [string, React.FC<any>];

const LoggedInDialogComponents: LoggedInDialogConfig[] = [
  [Dialogs.CONFIRMATION, ConfirmationDialog],
  [Dialogs.CONSTITUENT_BULK_EDIT_BOARDS, BulkEditBoardsDialog],
  [Dialogs.CONSTITUENT_BULK_EDIT_LABELS, BulkEditLabelsDialog],
  [Dialogs.CONSTITUENT_EXPORT_CHOICE, ConstituentExportChoiceDialog],
  [Dialogs.CONSTITUENT_EXPORT_CSV, ConstituentExportCSVDialog],
  [Dialogs.CONSTITUENT_EXPORT_DESTINATION, ConstituentExportDestinationDialog],
  [
    Dialogs.CONSTITUENT_LIFECYCLE_POSITIONS,
    ConstituentLifecyclePositionsDialog,
  ],
  [Dialogs.CREATE_CUSTOM_FIELD, CreateCustomFieldDialog],
  [Dialogs.CREATE_EDIT_OPPORTUNITY, CreateEditOpportunityDialog],
  [Dialogs.CREATE_LIFECYCLE_BOARD, CreateLifecycleBoardDialog],
  [Dialogs.DONATION_FORM, NewDonationDialog],
  [Dialogs.EXPORT_MESSAGE, ExportMessageDialog],
  [Dialogs.INFORMATIONAL, InformationalDialog],
  [Dialogs.LABELS_EDIT, EditLabelDialog],
  [Dialogs.LINK_TO_DONATIONS, LinkToDonationsDialog],
  [Dialogs.LINK_TO_OPPORTUNITY, LinkToOpportunityDialog],
  [Dialogs.LINK_TO_PLEDGE, LinkToPledgeDialog],
  [Dialogs.PLEDGE_INTEGRATION, PledgeIntegrationDialog],
  [Dialogs.CREATE_EDIT_CAMPAIGN_EVENT, CreateEditCampaignEventDialog],
  [Dialogs.CREATE_EDIT_TICKET_OPTION, CreateEditTicketOptionDialog],
  [Dialogs.MOVE_CAMPAIGN_EVENT, MoveCampaignEventDialog],
];

type DialogName = (typeof LoggedInDialogComponents)[number][0];

type DialogManagerContextState = {
  openDialog(name: DialogName, componentProps: any): void;
  closeDialog(): void;
};

export const DialogManagerContext = React.createContext(
  {} as DialogManagerContextState,
);
export const useDialogManager = () => React.useContext(DialogManagerContext);

type DialogManagerProviderProps = {
  children: React.ReactNode;
};

export default function DialogManagerProvider({
  children,
}: DialogManagerProviderProps) {
  const isAuthenticated = useSelector<RootState>(
    (state) => state.authentication?.isAuthenticated,
  );
  const [activeDialog, setActiveDialog] = React.useState<DialogName | ''>('');

  // This component provides the ability to switch from one dialog to another,
  // preserving the in and out transitions for both (vs having the exiting
  // dialog simply disappear). To achieve this, the dialog props are stored
  // in a state under a key that matches with the dialog's name. This allows
  // for multiple dialog props to be stored internally. The purpose for this
  // is that a dialog that is transitioning out can maintain whatever internal
  // state it needs while doing so. The alternative could potentially lead to
  // hard-to-debug  bugs.
  const [dialogProps, setDialogProps] = React.useState<{
    [key in DialogName]: Record<string, any>;
  }>({});

  const closeDialog = React.useCallback(() => {
    setActiveDialog('');
  }, []);

  const openDialog = React.useCallback(
    (
      name: DialogName,
      componentProps: {
        onClose?(e: React.SyntheticEvent): void;
      } = {},
    ) => {
      setActiveDialog(name);
      setDialogProps((prev) => ({
        ...prev,
        [name]: {
          ...componentProps,
          // Some dialogs may need to have their own "onClose" logic that they
          // need to run when close. Merge the component's "onClose" with the
          // "onClose" functionality of <DialogManagerProvider>.
          onClose: (e: React.SyntheticEvent) => {
            if (componentProps.onClose) {
              componentProps.onClose(e);
            }
            closeDialog();
          },
          TransitionProps: {
            mountOnEnter: true,
            unmountOnExit: true,
            onExited: () => {
              // Only remove the exiting dialog's props from the
              // internal state when it has completed it's exit.
              setDialogProps((prevState) => omit(name, prevState));
            },
          },
        },
      }));
    },
    [],
  );

  const value = React.useMemo(
    () => ({
      closeDialog,
      openDialog,
    }),
    [],
  );

  return (
    <DialogManagerContext.Provider value={value}>
      <>
        {children}
        {isAuthenticated &&
          LoggedInDialogComponents.map(([name, Component]) => (
            <Component
              data-testid={`${name}-dialog`}
              key={name}
              {...dialogProps[name]}
              open={name === activeDialog}
              isRendered={!!dialogProps[name]}
            />
          ))}
      </>
    </DialogManagerContext.Provider>
  );
}

DialogManagerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
